import { cn } from '@zond/utils';
import { chunk } from 'lodash-es';
import { Check, ChevronsUpDown } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import * as React from 'react';
import { FixedSizeList as List } from 'react-window';
import { Button } from './button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from './command';
import { Popover, PopoverContent, PopoverTrigger } from './popover';

type DynamicIconComponent = React.ComponentType<{ className?: string }>;

// Convert all Lucide icons into a format we can use
const icons = Object.entries(LucideIcons)
  .filter(
    ([name, component]) =>
      name !== 'createLucideIcon' &&
      name !== 'default' &&
      name !== 'Icon' &&
      typeof (component as any)?.render === 'function'
  )
  .map(([name, Icon]) => ({
    value: name,
    label: name.replace(/([A-Z])/g, ' $1').trim(), // Convert camelCase to words
    Icon: Icon as DynamicIconComponent,
  }));

export interface IconPickerProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
}

type IconItem = {
  value: string;
  label: string;
  Icon: DynamicIconComponent;
};

interface ItemData {
  items: IconItem[][]; // Array of arrays, each inner array has 4 icons
  value?: string;
  onSelect: (value: string) => void;
  iconsPerRow: number;
}

const ITEM_HEIGHT = 50; // Height of each row in pixels
const WINDOW_HEIGHT = 300; // Height of the virtualized window
const ICON_WIDTH = 40; // Width of each icon in pixels
const GRID_GAP = 8; // Gap between icons in pixels

const Row = React.memo(
  ({ index, style, data }: { index: number; style: React.CSSProperties; data: ItemData }) => {
    const { items, value, onSelect, iconsPerRow } = data;
    const row = items[index] || [];

    return (
      <div
        style={{
          ...style,
          display: 'grid',
          gridTemplateColumns: `repeat(auto-fill, ${ICON_WIDTH}px)`,
          gap: `${GRID_GAP}px`,
          justifyContent: 'space-between',
          padding: '0',
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
        }}
        className="grid px-2"
      >
        {row.map((item: IconItem) => {
          const Icon = item.Icon;
          return (
            <button
              key={item.value}
              onClick={() => onSelect(item.value)}
              className={cn(
                'w-10 h-10 flex items-center justify-center rounded-md hover:bg-accent',
                value === item.value && 'bg-accent'
              )}
            >
              <Icon className="h-6 w-6" />
            </button>
          );
        })}
      </div>
    );
  }
);

Row.displayName = 'IconPickerRow';

export function IconPicker({ value, onChange, placeholder = 'Select icon...' }: IconPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [iconsPerRow, setIconsPerRow] = React.useState(4);
  const buttonRef = React.useRef<HTMLButtonElement>(null);

  React.useEffect(() => {
    if (buttonRef.current) {
      const buttonWidth = buttonRef.current.offsetWidth;
      // Calculate how many icons can fit in the width, accounting for gaps
      // Total space taken by gaps is (n-1) * GRID_GAP where n is number of icons
      // So: buttonWidth = n * ICON_WIDTH + (n-1) * GRID_GAP
      // Solve for n: n = (buttonWidth + GRID_GAP) / (ICON_WIDTH + GRID_GAP)
      const calculatedIconsPerRow = Math.floor((buttonWidth + GRID_GAP) / (ICON_WIDTH + GRID_GAP));
      setIconsPerRow(Math.max(2, calculatedIconsPerRow)); // Minimum 2 icons per row
    }
  }, [open]); // Recalculate when popover opens

  const selectedIcon = React.useMemo(() => {
    if (!value) return null;
    return icons.find((icon) => icon.value === value);
  }, [value]);

  const filteredIcons = React.useMemo(() => {
    if (!searchQuery.trim()) return icons;
    const lowerQuery = searchQuery.toLowerCase().trim();
    return icons.filter(
      (icon) => icon.value.toLowerCase().includes(lowerQuery) || icon.label.toLowerCase().includes(lowerQuery)
    );
  }, [searchQuery]);

  const groupedBy4Data = React.useMemo(() => chunk(filteredIcons, iconsPerRow), [filteredIcons, iconsPerRow]);

  const itemData = React.useMemo(
    () => ({
      items: groupedBy4Data,
      value,
      onSelect: (currentValue: string) => {
        onChange?.(currentValue);
        setOpen(false);
      },
      iconsPerRow,
    }),
    [groupedBy4Data, value, onChange, iconsPerRow]
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();

      const list = document.querySelector('#icons-list');
      if (!list) return;

      // Get list element
      const items = Array.from(list.querySelectorAll('[data-highlighted]'));
      const activeItemIndex = items.findIndex((item) => item.getAttribute('data-highlighted') === 'true');

      // Get current scroll position
      const container = list.parentElement;
      if (!container) return;

      // Set new scroll position
      if (activeItemIndex !== -1) {
        const activeItem = items[activeItemIndex] as HTMLElement;
        // Prevent default scroll behavior
        container.scrollTop = activeItem.offsetTop - container.offsetHeight / 2 + activeItem.offsetHeight / 2;
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          ref={buttonRef}
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal px-3"
        >
          <div className="flex items-center gap-2">
            {selectedIcon?.Icon && (
              <>
                <selectedIcon.Icon className="h-6 w-6" />
                <span className="hidden sm:inline-block">{value?.replace(/([A-Z])/g, ' $1').trim()}</span>
              </>
            )}
            {!value && <span className="text-muted-foreground">{placeholder}</span>}
          </div>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        onOpenAutoFocus={(e) => e.preventDefault()}
        align="start"
        className="p-0 pointer-events-auto"
        style={{ width: 'var(--radix-popover-trigger-width)' }}
      >
        <Command>
          <CommandInput placeholder="Search icons..." value={searchQuery} onValueChange={setSearchQuery} />
          {filteredIcons.length === 0 && <CommandEmpty>No icon found.</CommandEmpty>}
          {filteredIcons.length > 0 && (
            <div
              className="custom-scrollbar pt-2"
              onWheel={(e) => {
                // Get list element
                const listElement = e.currentTarget.querySelector('.ReactWindowList');
                if (listElement) {
                  // Get current scroll position
                  const scrollTop = listElement.scrollTop;
                  // Set new scroll position
                  listElement.scrollTop = scrollTop + e.deltaY;
                  // Prevent default scroll behavior
                  e.preventDefault();
                }
              }}
            >
              <List
                height={WINDOW_HEIGHT}
                itemCount={groupedBy4Data.length}
                itemSize={ITEM_HEIGHT}
                width="100%"
                itemData={itemData}
                className="ReactWindowList"
              >
                {Row}
              </List>
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
